import { ChangeDetectionStrategy, Component, inject, input, InputSignal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { FieldInterventionEntityComponent } from '@connect-services/client/components/field-intervention-information/field-intervention-operator-or-status-metrics/field-intervention-entity/field-intervention-entity.component';
import { FieldInterventionOperatorComponent } from '@connect-services/client/components/field-intervention-information/field-intervention-operator-or-status-metrics/field-intervention-operator/field-intervention-operator.component';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { AssertService } from '@connect-services/client/services/assert.service';
import { FieldInterventionEntitiesFilterService } from '@connect-services/client/services/field-intervention-entities-filter.service';
import { FieldInterventionEntity } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import StatusEnum = FieldInterventionEntity.StatusEnum;

export class EntitiesDashboardInformations {
    public title?: string;
    public type?: EntitiesDashboardInformationTypeEnum;
}

export interface EntitiesDashboardInformationsByStatus extends EntitiesDashboardInformations {
    entities: DashboardInterventionEntitiesByStatus[];
    type: EntitiesDashboardInformationTypeEnum.BY_STATUS;
}

export class DashboardInterventionEntitiesByStatus {
    public count?: number;
    public percentage?: number;
    public status?: FieldInterventionEntity.StatusEnum;
}

export interface EntitiesDashboardInformationsByOperator extends EntitiesDashboardInformations {
    entities: DashboardInterventionEntityByOperator[];
    type: EntitiesDashboardInformationTypeEnum.BY_OPERATOR;
}

export class DashboardInterventionEntityByOperator {
    public count?: number;
    public firstname?: string;
    public icon?: string;
    public id?: string;
    public lastname?: string;
    public percentage?: number;
}

export enum EntitiesDashboardInformationTypeEnum {
    BY_STATUS = 'BY_STATUS',
    BY_OPERATOR = 'BY_OPERATOR',
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FieldInterventionEntityComponent, FieldInterventionOperatorComponent, RessourcesModule],
    selector: 'app-field-intervention-operator-or-status-metrics',
    styleUrl: './field-intervention-operator-or-status-metrics.component.scss',
    templateUrl: './field-intervention-operator-or-status-metrics.component.html',
})
export class FieldInterventionOperatorOrStatusMetricsComponent {
    public readonly fieldInterventionEntities: InputSignal<EntitiesDashboardInformationsByStatus | undefined> = input<
        EntitiesDashboardInformationsByStatus | undefined
    >();
    public readonly fieldInterventionOperators: InputSignal<EntitiesDashboardInformationsByOperator | undefined> =
        input<EntitiesDashboardInformationsByOperator | undefined>();
    public readonly title: InputSignal<string | undefined> = input<string | undefined>();

    // Status classification by business logic
    protected readonly customStatusOrder: FieldInterventionEntity.StatusEnum[] = [
        'planned',
        'ongoing',
        'toValidate',
        'done',
        'invalid',
    ];
    protected EntitiesDashboardInformationTypeEnum = EntitiesDashboardInformationTypeEnum;
    protected readonly filterEntitiesTableService = inject(FieldInterventionEntitiesFilterService);

    private readonly activateRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);

    public getSortedEntitiesByStatus(): DashboardInterventionEntitiesByStatus[] {
        const entities = this.fieldInterventionEntities()?.entities ?? [];

        return entities.toSorted(
            (a: DashboardInterventionEntitiesByStatus, b: DashboardInterventionEntitiesByStatus) =>
                this.customStatusOrder.indexOf(a.status!) - this.customStatusOrder.indexOf(b.status!),
        );
    }

    public async openEntitiesFilteredTable(
        filterByType: EntitiesDashboardInformationTypeEnum,
        filterValue: unknown,
    ): Promise<void> {
        this.filterEntitiesTableService.resetData();
        switch (filterByType) {
            case EntitiesDashboardInformationTypeEnum.BY_STATUS:
                AssertService.isEnum<StatusEnum>(filterValue, StatusEnum);
                this.filterEntitiesTableService.toggleStatusSelection(filterValue, { forceToggle: true });
                break;
            case EntitiesDashboardInformationTypeEnum.BY_OPERATOR:
                AssertService.isString(filterValue);
                this.filterEntitiesTableService.toggleSelection(filterValue, 'people');
                break;
            default:
                break;
        }

        await this.router.navigate([`../${RoutePathEnum.FIELD_INTERVENTION_ENTITIES}`], {
            queryParamsHandling: 'merge',
            relativeTo: this.activateRoute,
        });
    }
}
